import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { studentReportService } from '../services/StudentReportService';
import MapView from './MapView';
import { schoolMatches } from '../utils/schoolScope';
import './AdminStudentReportsView.css';
import './StudentDetailPages.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Reports' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' }
];

function SuperAdminStudentReportsView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  // Dropdown is authoritative: "All Schools" ('' ) = national view
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || null }), [adminData, saSchoolId]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    const unsubscribe = studentReportService.subscribeToReports((adminData?.schoolIds?.length ? adminData.schoolIds : adminData?.schoolId) || null, (data) => {
      setReports(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [adminData]);

  const filteredReports = useMemo(() => {
    if (statusFilter === 'all') return reports;
    return reports.filter(r => (r.status || 'pending').toLowerCase() === statusFilter);
  }, [reports, statusFilter]);

  const mapMarkers = useMemo(() => {
    return filteredReports
      .filter(r => Number.isFinite(r.latitude) && Number.isFinite(r.longitude) && (r.latitude !== 0 || r.longitude !== 0))
      .map(r => ({
        lat: r.latitude,
        lng: r.longitude,
        color: (r.status || 'pending').toLowerCase() === 'resolved' ? '#10b981' : '#ef4444',
        label: `<div><strong>${r.studentName || 'Unknown'}</strong><br/>${r.reason || 'No reason'}<br/>${r.locationName || ''}<br/>${new Date(r.createdAt).toLocaleString()}</div>`
      }));
  }, [filteredReports]);

  const handleResolve = async () => {
    if (!selectedReport) return;
    setResolving(true);
    setError('');
    try {
      await studentReportService.resolveReport(selectedReport.id, adminData?.uid || '', resolutionNotes);
      setSelectedReport(null);
      setResolutionNotes('');
    } catch (err) {
      console.error('Error resolving report:', err);
      setError('Failed to resolve report. Please try again.');
    } finally {
      setResolving(false);
    }
  };

  const schoolNameFor = (sid) => saSchools.find(s => schoolMatches(s.id, sid))?.name || '';

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading reports...</p>
      </div>
    );
  }

  if (selectedReport) {
    const r = selectedReport;
    const statusKey = (r.status || 'pending').toLowerCase();
    console.log('🔥 SuperAdminStudentReports: Opened report detail', r.id);
    return (
      <div className="detail-page">
        <button className="detail-back-btn" onClick={() => { setSelectedReport(null); setResolutionNotes(''); setError(''); }}>◄ Back</button>
        <div className="detail-hero">
          <div className="detail-hero-icon">🚨</div>
          <div>
            <h2 className="detail-page-title">
              {r.studentName || 'Unknown Student'}
              <span className={`detail-badge ${statusKey}`}>{statusKey}</span>
            </h2>
            <p className="detail-page-subtitle">{r.reason || 'Student report'} · {new Date(r.createdAt).toLocaleString()}</p>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="detail-card">
          <h4>Report Details</h4>
          <div className="detail-rows">
            <div className="detail-row"><span className="detail-label">Student</span><span className="detail-value">{r.studentName || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Reason</span><span className="detail-value">{r.reason || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Location</span><span className="detail-value">{r.locationName || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Scanner</span><span className="detail-value">{r.scannerName || r.scannerId || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">School</span><span className="detail-value">{schoolNameFor(r.schoolId) || r.schoolId || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Reported</span><span className="detail-value">{new Date(r.createdAt).toLocaleString()}</span></div>
            {statusKey === 'resolved' && (
              <div className="detail-row"><span className="detail-label">Resolved</span><span className="detail-value">{r.resolvedAt ? new Date(r.resolvedAt).toLocaleString() : ''}{r.resolvedBy ? ` by ${r.resolvedBy}` : ''}</span></div>
            )}
          </div>
        </div>

        {r.notes && (
          <div className="detail-card">
            <h4>Notes</h4>
            <p className="detail-note">{r.notes}</p>
          </div>
        )}

        {statusKey !== 'resolved' && (
          <div className="detail-card">
            <h4>Resolve Report</h4>
            <div className="form-group">
              <label>Resolution Notes</label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Add notes before resolving..."
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="detail-actions">
              <button
                className="btn-approve"
                onClick={handleResolve}
                disabled={resolving}
              >
                {resolving ? 'Resolving...' : 'Mark Resolved'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="admin-student-reports-view">
      <div className="view-header">
        <h2>Student Reports</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        <select
          className="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="map-section">
        <MapView markers={mapMarkers} height={320} defaultZoom={14} />
      </div>

      {filteredReports.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No reports found</h3>
          <p>Student reports submitted from the iOS scanner will appear here.</p>
        </div>
      ) : (
        <div className="reports-list">
          {filteredReports.map(report => (
            <div
              key={report.id}
              className={`report-card ${(report.status || 'pending').toLowerCase() === 'resolved' ? 'resolved' : 'pending'}`}
              onClick={() => setSelectedReport(report)}
            >
              <div className="report-main">
                <h4>{report.studentName || 'Unknown Student'}</h4>
                <span className="report-reason">{report.reason}</span>
                <span className="report-location">{report.locationName || 'Unknown location'}</span>
                {(schoolNameFor(report.schoolId) || report.schoolId) && (
                  <span className="report-location">🏫 {schoolNameFor(report.schoolId) || report.schoolId}</span>
                )}
              </div>
              <div className="report-meta">
                <span className={`status-badge ${report.status || 'pending'}`}>{report.status || 'pending'}</span>
                <span className="report-time">{new Date(report.createdAt).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default SuperAdminStudentReportsView;
